import { ActionKind, Icon } from '@dynamic/ui';
import React, { FC, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { LogoMvpd } from '#/components/Logo';
import {
  useActions,
  useConfig,
  useIsMobile,
  useNavigation,
  useScrollControl,
} from '#/hooks';
import { THamburgerItemsData } from '#/service/config';
import { MenuFocusStyle } from '#/service/constants';
import { InternalPageId } from '#/service/constants/menu';
import { remify } from '#/service/utils';

import { linkStyle, MenuGradient, MenuNav, transProps } from './components';
import { ItemLink } from './ItemLink';
import {
  findMenuItemByDestinationId,
  getDefaultMenuItemId,
  getMenuItemInformation,
  MenuProps,
} from './utils';

const showIcons = true;
const menuContentsId = 'hamburger-menu-contents';

interface MenuLogoProps {
  isCollapsed?: boolean;
  src: string;
  alt: string;
}

const HamburgerMenuNav = styled(MenuNav)`
  inline-size: calc(100% - ${remify(12)});
  height: ${remify(80)};
  padding-left: ${remify(6)};
  padding-right: ${remify(6)};
`;

HamburgerMenuNav.displayName = 'HamburgerMenuNav';

const MenuLogo = styled.img.attrs(
  (props): MenuLogoProps => ({
    src: props.theme.logo?.primary ?? props.theme.images?.appLogo ?? '',
    alt: 'logo',
  })
)`
  padding: 0;
  top: 0;
  bottom: ${remify(80)};
  left: 0;
  right: 0;
  margin-block-start: auto;
  margin-block-end: auto;
  ${(props) => !props.src && 'display: none;'}
  max-inline-size: ${remify(180)};
  max-height: ${remify(60)};
  max-width: ${remify(100)};
  transition: bottom ${transProps};
  cursor: pointer;
  ${(props) =>
    props.isCollapsed &&
    css`
      bottom: 0;
    `};
`;

MenuLogo.displayName = 'MenuLogo';

const MenuItemsContainer = styled.div`
  max-inline-size: ${remify(348)};
  block-size: 100%;
  background-color: var(--menuOpaqueBackground);
  box-sizing: border-box;
  padding: 0 ${remify(31)};
  pointer-events: auto;

  > button {
    margin-left: ${remify(-25)};
  }

  > div {
    padding-bottom: 0;
  }

  & .box .icon {
    height: 1.3rem;
    width: 1.3rem;
    margin-right: ${remify(24)};
  }
`;

MenuItemsContainer.displayName = 'MenuItemsContainer';

const MenuButton = styled.button`
  background: none;
  border: none;
  outline: none;
  height: 100%;
  ${linkStyle};
  padding: 0 ${remify(25)}!important;
  cursor: pointer;
`;

MenuButton.displayName = 'MenuButton';

const MenuOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100vh;
  background-color: var(--menuGradientBackground);
  pointer-events: auto;
`;

MenuOverlay.displayName = 'MenuOverlay';

const UserProfileDiv = styled.div`
  margin-block-end: ${remify(30)};
`;

UserProfileDiv.displayName = 'UserProfileDiv';

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0;
  margin-left: auto;

  & > a,
  button {
    padding: 0 ${remify(24)};
  }
`;

IconsContainer.displayName = 'IconsContainer';

const preventClick = (e: React.MouseEvent<HTMLDivElement>) => {
  e.stopPropagation();
};

interface HamburgerButtonProps {
  onClick: () => void;
  focusStyle?: MenuFocusStyle;
  isOpen: boolean;
}

const HamburgerButton = ({
  isOpen,
  onClick,
  focusStyle,
}: HamburgerButtonProps) => {
  // TODO: translations - add open/close aria-label
  const icon = isOpen ? 'line_3_horizontal' : 'cross';

  return (
    <MenuButton
      $focusStyle={focusStyle}
      aria-controls={menuContentsId}
      aria-expanded={isOpen}
      onClick={onClick}
    >
      <Icon key="icon" name={icon} />
    </MenuButton>
  );
};

const HamburgerItems = ({
  mainMenuItems = [],
  linkMenuItems = [],
  showItems = false,
  toggleHamburger,
  focusStyle,
}: THamburgerItemsData) => {
  const { actions } = useActions();
  const isMobile = useIsMobile();
  const defaultSelectedItem = mainMenuItems[0]?.id;

  const filteredIconItems = linkMenuItems.filter((item) => {
    const { destinationId } = getMenuItemInformation(item);

    return (
      destinationId !== InternalPageId.Search &&
      destinationId !== InternalPageId.More
    );
  });

  if (!showItems) {
    return null;
  }

  // If there is a login button in the main menu, we must make it open login popup.
  // there is no page to route to
  const getCallback = (id?: string) => {
    toggleHamburger();

    if (id === InternalPageId.Login) {
      actions[ActionKind.Login]();
    }
  };

  return (
    <MenuOverlay onClick={toggleHamburger}>
      <MenuItemsContainer id={menuContentsId} onClick={preventClick}>
        <HamburgerButton
          focusStyle={focusStyle}
          isOpen={false}
          onClick={toggleHamburger}
        />
        {mainMenuItems.map((item) => (
          <ItemLink
            defaultSelectedItem={defaultSelectedItem}
            inHamburger
            item={item}
            key={item.id}
            onClick={toggleHamburger}
            showIcons={showIcons}
          />
        ))}
        <UserProfileDiv>
          {filteredIconItems.map((item) => (
            <ItemLink
              defaultSelectedItem={defaultSelectedItem}
              inHamburger
              item={item}
              key={item.id}
              onClick={() => getCallback(item?.action?.params?.destination?.id)}
              showIcons={showIcons}
            />
          ))}
        </UserProfileDiv>
        {isMobile && <LogoMvpd isCentered />}
      </MenuItemsContainer>
    </MenuOverlay>
  );
};

const HamburgerMenu: FC<MenuProps> = (props) => {
  const {
    mainMenuItems = [],
    linkMenuItems = [],
    isCollapsed = false,
    isHidden = false,
  } = props;

  const { goHome } = useNavigation('HamburgerMenu');
  const [showItems, setShowItems] = useState(false);
  const { enableScroll, disableScroll } = useScrollControl();
  const defaultMenuItemId = getDefaultMenuItemId(mainMenuItems);
  const {
    theme: {
      menu: { focus: focusStyle },
    },
  } = useConfig();
  const isMobile = useIsMobile();

  const toggleHamburger = () => {
    (showItems ? enableScroll : disableScroll)();
    setShowItems(!showItems);
  };

  const extraItems = [
    findMenuItemByDestinationId(linkMenuItems, InternalPageId.Search),
    findMenuItemByDestinationId(linkMenuItems, InternalPageId.More),
  ].filter(Boolean);

  // Hide the menu on the playback page.
  // TODO: i think there is a spec to handle this genericly, but it's not implemented yet.
  const location = useLocation();

  if (location.pathname.startsWith('/playback')) {
    return null;
  }

  return (
    <>
      <HamburgerMenuNav $isCollapsed={isCollapsed} $isHidden={isHidden}>
        <MenuGradient />
        <HamburgerButton
          focusStyle={focusStyle}
          isOpen
          onClick={toggleHamburger}
        />
        <MenuLogo isCollapsed={isCollapsed} onClick={goHome} />
        <IconsContainer>
          {!isMobile && <LogoMvpd />}
          {extraItems.map(
            (item) =>
              item && (
                <ItemLink
                  defaultSelectedItem={defaultMenuItemId}
                  item={item}
                  key={item.id}
                  showIcons={showIcons}
                />
              )
          )}
        </IconsContainer>
      </HamburgerMenuNav>
      <HamburgerItems
        focusStyle={focusStyle}
        linkMenuItems={linkMenuItems}
        mainMenuItems={mainMenuItems}
        showItems={showItems}
        toggleHamburger={toggleHamburger}
      />
    </>
  );
};

export default HamburgerMenu;
